import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Button, Chip, Container, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

export default function HeroSection() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [ref] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Tally script yükleme
    useEffect(() => {
        if (!document.querySelector('script[src="https://tally.so/widgets/embed.js"]')) {
            const script = document.createElement('script');
            script.src = 'https://tally.so/widgets/embed.js';
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    return (
        <Box
            sx={{
                minHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                background: 'white',
                pt: { xs: 10, md: 0 },
                pb: 3.125,
                overflow: 'hidden',
                color: theme.palette.text.primary,
            }}
        >
            {/* Animasyonlu arka plan öğeleri */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    overflow: 'hidden',
                    opacity: 0.05
                }}
            >
                {/* Parlayan noktalar */}
                {Array.from({ length: 30 }).map((_, index) => (
                    <Box
                        key={index}
                        component={motion.div}
                        sx={{
                            position: 'absolute',
                            width: Math.random() * 4 + 1,
                            height: Math.random() * 4 + 1,
                            borderRadius: '50%',
                            background: theme.palette.primary.main,
                            boxShadow: `0 0 10px 2px ${theme.palette.primary.main}`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            zIndex: 0,
                            opacity: Math.random() * 0.5 + 0.2,
                            display: { xs: index > 15 ? 'none' : 'block', md: 'block' }
                        }}
                        animate={{
                            opacity: [
                                Math.random() * 0.5 + 0.2,
                                Math.random() * 0.8 + 0.5,
                                Math.random() * 0.5 + 0.2
                            ],
                        }}
                        transition={{
                            duration: Math.random() * 3 + 2,
                            repeat: Infinity,
                            repeatType: 'reverse',
                        }}
                    />
                ))}

                {/* Büyük gradyan daireler */}
                {Array.from({ length: 5 }).map((_, index) => (
                    <Box
                        key={`circle-${index}`}
                        component={motion.div}
                        sx={{
                            position: 'absolute',
                            width: `${Math.random() * 300 + 200}px`,
                            height: `${Math.random() * 300 + 200}px`,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, rgba(255,96,0,0.1) 0%, rgba(255,96,0,0) 70%)`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            zIndex: 0,
                            opacity: 0.4,
                            display: { xs: index > 2 ? 'none' : 'block', md: 'block' }
                        }}
                        animate={{
                            x: [0, Math.random() * 50 - 25],
                            y: [0, Math.random() * 50 - 25],
                        }}
                        transition={{
                            duration: Math.random() * 20 + 10,
                            repeat: Infinity,
                            repeatType: 'reverse',
                            ease: "easeInOut"
                        }}
                    />
                ))}
            </Box>

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center" direction={isMobile ? 'column-reverse' : 'row'}>
                    <Grid item xs={12} md={6}>
                        <motion.div
                            ref={ref}
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            viewport={{ once: true }}
                        >
                            <Chip
                                label="YAPAY ZEKA DESTEKLI"
                                size="medium"
                                sx={{
                                    bgcolor: 'rgba(255,96,0,0.1)',
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    mb: 3,
                                    px: 1,
                                    borderRadius: '50px',
                                    '& .MuiChip-label': {
                                        px: 1.5
                                    }
                                }}
                            />

                            <Typography
                                variant="h1"
                                component="h1"
                                sx={{
                                    fontSize: { xs: '2.5rem', sm: '3.25rem', md: '4rem' },
                                    fontWeight: 800,
                                    mb: 2,
                                    color: theme.palette.secondary.main,
                                    lineHeight: 1.1,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Pazaryerleri Sorularını <Box component="span" sx={{ color: theme.palette.primary.main }}>Otomatik</Box> Yanıtlayın
                            </Typography>

                            <Typography
                                variant="h2"
                                component="h2"
                                sx={{
                                    fontSize: { xs: '1.25rem', md: '1.75rem' },
                                    fontWeight: 600,
                                    mb: 3,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.3,
                                }}
                            >
                                Yapay Zeka ile Satışlarınızı Artırın
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.15rem' },
                                    color: theme.palette.text.secondary,
                                    mb: 5,
                                    maxWidth: '600px',
                                    lineHeight: 1.6,
                                }}
                            >
                                TrendAsist, Pazaryerleri mağazanızdaki müşteri sorularını yapay zeka ile otomatik yanıtlayarak satışlarınızı artırır ve zamandan tasarruf etmenizi sağlar. 7/24 çalışan asistanınız ile müşteri memnuniyetini en üst seviyeye çıkarın.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 } }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForwardIcon />}
                                    data-tally-open="wMV9kA"
                                    data-tally-layout="modal"
                                    data-tally-width="400"
                                    data-tally-auto-close="0"
                                    sx={{
                                        py: { xs: 1.5, md: 2 },
                                        px: { xs: 3, md: 4 },
                                        borderRadius: '50px',
                                        fontSize: { xs: '0.9rem', md: '1rem' },
                                        fontWeight: 600,
                                        color: 'white',
                                        boxShadow: '0 10px 20px rgba(255, 96, 0, 0.2)',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: '0 15px 30px rgba(255, 96, 0, 0.3)',
                                        },
                                        transition: 'transform 0.3s, box-shadow 0.3s',
                                    }}
                                >
                                    Hemen Başlayın
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<PlayArrowIcon />}
                                    component="a"
                                    href="https://www.youtube.com/watch?v=FywWrhGtON4&t=277s"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        py: { xs: 1.5, md: 2 },
                                        px: { xs: 3, md: 4 },
                                        borderRadius: '50px',
                                        fontSize: { xs: '0.9rem', md: '1rem' },
                                        fontWeight: 600,
                                        borderColor: theme.palette.primary.main,
                                        color: theme.palette.primary.main,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            borderColor: theme.palette.primary.dark,
                                            backgroundColor: 'rgba(255, 96, 0, 0.05)',
                                        },
                                    }}
                                >
                                    Nasıl Çalışır?
                                </Button>
                            </Box>
                        </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                            viewport={{ once: true }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                {/* Parlama efekti */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        background: 'radial-gradient(circle, rgba(255,96,0,0.15) 0%, rgba(255,96,0,0) 70%)',
                                        filter: 'blur(40px)',
                                        top: 0,
                                        left: 0,
                                        zIndex: 0,
                                    }}
                                />

                                <Box
                                    component="img"
                                    src="https://i.hizliresim.com/2xwjhyw.png"
                                    alt="TrendAsist Dashboard"
                                    sx={{
                                        width: '100%',
                                        maxWidth: '600px',
                                        height: 'auto',
                                        borderRadius: '24px',
                                        boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                                        transform: 'perspective(1500px) rotateY(-12deg) rotateX(5deg)',
                                        transition: 'transform 0.5s',
                                        '&:hover': {
                                            transform: 'perspective(1500px) rotateY(-5deg) rotateX(2deg)',
                                        },
                                        mx: 'auto',
                                        display: 'block',
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                />
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
} 